/**
 * CustomerForm — Slide-over panel for creating/editing AML customers.
 */
import React, { useReducer, useCallback, useMemo, useState, useEffect } from 'react';
import Plus from 'lucide-react/dist/esm/icons/plus';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import { PatternFormat } from 'react-number-format';
import { useAmlStore } from '../../stores/amlStore';
import { amlApi } from '../../services/amlApi';
import { DatePicker } from '../../../../components/ui';
import { Button, IconButton, TextField, TextArea, SlidePanel, Text, Switch, NumericStepper } from '../../../../components/ui';
import { useToast } from '../../../../components/feedback';
import { useUIStore } from '../../../../stores/uiStore';
import { getTranslation } from '../../../../i18n';
import type { AmlCustomer } from '../../types/aml';

interface CustomerFormProps {
  customer: AmlCustomer | null;
  onClose: () => void;
}

interface FormErrors {
  name?: string;
  code?: string;
  initial_date?: string;
  established_date?: string;
  locations?: string;
  address?: string;
  legal_representative?: string;
  contact?: string;
  email?: string;
}

const PHONE_EMPTY = '001';
const TODAY_ISO = new Date().toISOString().slice(0, 10);

const RE_LETTERS_ONLY = /^[a-zA-Z\s,]*$/;
const RE_ALPHANUMERIC = /^[a-zA-Z0-9\s]*$/;
const RE_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

interface FormState {
  name: string;
  code: number | '';
  status: boolean;
  initial_date: string;
  ein_or_fein: string;
  registration_number: string;
  established_date: string;
  legal_representative: string;
  address: string;
  service_and_product: string;
  activity_type: string;
  contact: string;
  email: string;
  phone: string;
  notes: string;
  locations: { id?: string; name: string }[];
  submitting: boolean;
  errors: FormErrors;
}

const INITIAL_STATE: FormState = {
  name: '',
  code: '',
  status: true,
  initial_date: '',
  ein_or_fein: '',
  registration_number: '',
  established_date: '',
  legal_representative: '',
  address: '',
  service_and_product: '',
  activity_type: '',
  contact: '',
  email: '',
  phone: PHONE_EMPTY,
  notes: '',
  locations: [],
  submitting: false,
  errors: {},
};

type FormAction =
  | { type: 'SET_FIELD'; field: keyof FormState; value: unknown }
  | { type: 'CLEAR_ERROR'; field: keyof FormErrors }
  | { type: 'SET_ERRORS'; errors: FormErrors }
  | { type: 'RESET'; payload: FormState }
  | { type: 'ADD_LOCATION' }
  | { type: 'REMOVE_LOCATION'; index: number }
  | { type: 'UPDATE_LOCATION'; index: number; value: string }
  | { type: 'RESTORE_LOCATIONS'; serverLocations: { id: string; name: string }[] };

function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case 'SET_FIELD':
      return { ...state, [action.field]: action.value };
    case 'CLEAR_ERROR':
      return { ...state, errors: { ...state.errors, [action.field]: undefined } };
    case 'SET_ERRORS':
      return { ...state, errors: action.errors };
    case 'RESET':
      return action.payload;
    case 'ADD_LOCATION':
      return { ...state, locations: [...state.locations, { name: '' }] };
    case 'REMOVE_LOCATION':
      return { ...state, locations: state.locations.filter((_, i) => i !== action.index) };
    case 'UPDATE_LOCATION':
      return {
        ...state,
        locations: state.locations.map((l, i) =>
          i === action.index ? { ...l, name: action.value } : l,
        ),
      };
    case 'RESTORE_LOCATIONS': {
      // Re-sync against the authoritative server list after a failed save:
      // keep the user's in-place renames (matched by id) and brand-new rows
      // (no id), while re-adding any persisted location that was removed
      // locally but rejected by the backend (e.g. it still has transactions).
      const editedById = new Map(
        state.locations.filter((l) => l.id).map((l) => [l.id, l]),
      );
      const fromServer = action.serverLocations.map(
        (sl) => editedById.get(sl.id) ?? { id: sl.id, name: sl.name },
      );
      const brandNew = state.locations.filter((l) => !l.id);
      return { ...state, locations: [...fromServer, ...brandNew] };
    }
    default:
      return state;
  }
}

function buildStateFromCustomer(customer: AmlCustomer | null): FormState {
  if (!customer) return INITIAL_STATE;
  return {
    name: customer.name,
    code: customer.code,
    status: customer.status,
    initial_date: customer.initial_date?.slice(0, 10) || '',
    ein_or_fein: customer.ein_or_fein || '',
    registration_number: customer.registration_number || '',
    established_date: customer.established_date?.slice(0, 10) || '',
    legal_representative: customer.legal_representative || '',
    address: customer.address || '',
    service_and_product: customer.service_and_product || '',
    activity_type: customer.activity_type || '',
    contact: customer.contact || '',
    email: customer.email || '',
    phone: customer.phone || PHONE_EMPTY,
    notes: customer.notes || '',
    locations: customer.locations?.map((l) => ({ id: l.id, name: l.name })) ?? [],
    submitting: false,
    errors: {},
  };
}

function validate(
  state: FormState,
  t: (key: string) => string,
  opts: {
    isEdit: boolean;
    editingCustomerId?: string;
    customers: { id: string; name: string }[];
  },
): FormErrors {
  const errs: FormErrors = {};

  const trimmedName = state.name.trim();
  if (!trimmedName) errs.name = t('amlCustomerValidationNameRequired');
  else {
    const nameTaken = opts.customers.some(
      (c) =>
        c.name === trimmedName &&
        (!opts.isEdit || c.id !== opts.editingCustomerId),
    );
    if (nameTaken) errs.name = t('amlCustomerValidationDuplicateLegalName');
  }
  if (state.code === '' || state.code === null || state.code === undefined) {
    errs.code = t('amlCustomerValidationCodeRequired');
  } else if (isNaN(Number(state.code))) {
    errs.code = t('amlCustomerValidationCodeMustBeNumber');
  }
  if (!state.initial_date) {
    errs.initial_date = t('amlCustomerValidationInitialDateRequired');
  } else if (state.initial_date < TODAY_ISO) {
    errs.initial_date = t('amlCustomerValidationDateCannotBePast');
  }
  if (state.established_date) {
    if (state.established_date > TODAY_ISO) {
      errs.established_date = t('amlCustomerValidationDateCannotBeFuture');
    }
    if (state.established_date < '1900-01-01') {
      errs.established_date = t('amlCustomerValidationInvalidDate');
    }
  }
  if (state.address && state.address.trim().length > 0 && state.address.trim().length < 3) {
    errs.address = t('amlCustomerValidationMustBeAtLeast3Chars');
  }
  if (state.legal_representative && !RE_LETTERS_ONLY.test(state.legal_representative)) {
    errs.legal_representative = t('amlCustomerValidationOnlyLettersAllowed');
  }
  if (state.contact && !RE_ALPHANUMERIC.test(state.contact)) {
    errs.contact = t('amlCustomerValidationOnlyLettersNumbers');
  }
  if (state.email && !RE_EMAIL.test(state.email)) {
    errs.email = t('amlCustomerValidationValidEmail');
  }
  const validLocations = state.locations.filter((l) => l.name.trim());
  if (state.locations.length > 0 && validLocations.length < state.locations.length) {
    errs.locations = t('amlCustomerValidationAllLocationNamesRequired');
  }
  return errs;
}

const CustomerForm: React.FC<CustomerFormProps> = ({ customer, onClose }) => {
  const { createCustomer, updateCustomer, loading, customers } = useAmlStore();
  const { success, error: toastError } = useToast();
  const { language } = useUIStore();
  const t = useCallback((key: string) => getTranslation(language, key), [language]);
  const isEdit = !!customer;

  const [state, dispatch] = useReducer(formReducer, customer, buildStateFromCustomer);

  // Ids of locations that already have transactions. Their remove control is
  // hidden because deleting them would destroy transaction history (and is
  // rejected by the backend guard anyway).
  const [lockedLocationIds, setLockedLocationIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!customer) return;
    let cancelled = false;
    amlApi
      .getCustomerLocationUsage(customer.id)
      .then((res) => {
        if (!cancelled) setLockedLocationIds(new Set(res.location_ids));
      })
      .catch(() => { /* non-critical: fall back to showing the control */ });
    return () => { cancelled = true; };
  }, [customer]);

  const setField = useCallback(
    <K extends keyof FormState>(field: K, value: FormState[K], clearError?: keyof FormErrors) => {
      dispatch({ type: 'SET_FIELD', field, value });
      if (clearError) dispatch({ type: 'CLEAR_ERROR', field: clearError });
    },
    [],
  );

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate(state, t, {
      isEdit,
      editingCustomerId: customer?.id,
      customers,
    });
    if (Object.keys(errs).length > 0) {
      dispatch({ type: 'SET_ERRORS', errors: errs });
      return;
    }
    dispatch({ type: 'SET_ERRORS', errors: {} });
    dispatch({ type: 'SET_FIELD', field: 'submitting', value: true });
    try {
      const phoneVal = state.phone === PHONE_EMPTY || !state.phone ? null : state.phone;
      const data = {
        name: state.name.trim(),
        code: Number(state.code),
        status: state.status,
        initial_date: state.initial_date,
        ein_or_fein: state.ein_or_fein || null,
        registration_number: state.registration_number || null,
        established_date: state.established_date || null,
        legal_representative: state.legal_representative || null,
        address: state.address || null,
        service_and_product: state.service_and_product || null,
        activity_type: state.activity_type || null,
        contact: state.contact || null,
        email: state.email || null,
        phone: phoneVal,
        notes: state.notes || null,
        locations: state.locations
          .filter((l) => l.name.trim())
          .map((l) => (l.id ? { id: l.id, name: l.name.trim() } : { name: l.name.trim() })),
      };

      if (isEdit && customer) {
        await updateCustomer(customer.id, data);
        success(t('amlCustomerToastUpdated'));
      } else {
        await createCustomer(data);
        success(t('amlCustomerToastCreated'));
      }
      onClose();
    } catch (err: any) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      const dupMsg = t('amlCustomerValidationDuplicateLegalName');
      if (
        status === 409 &&
        typeof detail === 'string' &&
        detail.toLowerCase().includes('legal name')
      ) {
        dispatch({ type: 'SET_ERRORS', errors: { name: dupMsg } });
        toastError(dupMsg);
      } else {
        const msg = detail
          ? (Array.isArray(detail)
              ? detail.map((d: any) => d.msg || d.message || JSON.stringify(d)).join('; ')
              : detail)
          : t(isEdit ? 'amlCustomerToastUpdated' : 'amlCustomerToastCreated');
        toastError(msg);
        // The save was rejected and nothing persisted, so a location the user
        // removed locally still exists on the server. Restore it into the form
        // so the list reflects reality without needing to reopen the panel.
        if (
          status === 409 &&
          typeof detail === 'string' &&
          detail.toLowerCase().includes('cannot remove location') &&
          customer
        ) {
          dispatch({
            type: 'RESTORE_LOCATIONS',
            serverLocations: customer.locations.map((l) => ({ id: l.id, name: l.name })),
          });
        }
      }
    } finally {
      dispatch({ type: 'SET_FIELD', field: 'submitting', value: false });
    }
  }, [state, t, isEdit, customer, customers, updateCustomer, createCustomer, success, toastError, onClose]);

  const addLocation = useCallback(() => dispatch({ type: 'ADD_LOCATION' }), []);

  const removeLocation = useCallback(
    (idx: number) => dispatch({ type: 'REMOVE_LOCATION', index: idx }),
    [],
  );

  const handleLocationChange = useCallback(
    (idx: number, value: string) => {
      dispatch({ type: 'UPDATE_LOCATION', index: idx, value });
      dispatch({ type: 'CLEAR_ERROR', field: 'locations' });
    },
    [],
  );

  const panelTitle = useMemo(
    () => isEdit ? t('amlCustomerFormTitleEdit') : t('amlCustomerFormTitleCreate'),
    [isEdit, t],
  );

  return (
    <SlidePanel isOpen={true} onClose={onClose} title={panelTitle} width="max-w-4xl">
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TextField
            label={t('amlCustomerFieldCustomerLegalName')}
            required
            value={state.name}
            onChange={(e) => setField('name', e.target.value, 'name')}
            placeholder={t('amlCustomerPlaceholderLegalName')}
            error={state.errors.name}
          />
          <NumericStepper
            label={t('amlCustomerFieldCode')}
            required
            value={state.code === '' ? '' : String(state.code)}
            onChange={(v) => setField('code', v === '' ? '' : Number(v), 'code')}
            placeholder={t('amlCustomerPlaceholderCode')}
            error={state.errors.code}
            min={0}
          />
          <TextField
            label={t('amlCustomerFieldRegistrationNumber')}
            value={state.registration_number}
            onChange={(e) => setField('registration_number', e.target.value)}
            placeholder={t('amlCustomerPlaceholderRegistrationNumber')}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TextField
            label={t('amlCustomerFieldEinFein')}
            value={state.ein_or_fein}
            onChange={(e) => setField('ein_or_fein', e.target.value)}
            placeholder={t('amlCustomerPlaceholderEinFein')}
          />
          <div>
            <Text variant="label" className="block mb-1.5">
              {t('amlCustomerFieldInitialDate')} <span className="text-status-error">*</span>
            </Text>
            <DatePicker
              value={state.initial_date}
              onChange={(v) => setField('initial_date', v, 'initial_date')}
              placeholder={t('amlCustomerPlaceholderSelectInitialDate')}
              minDate={TODAY_ISO}
            />
            {state.errors.initial_date && (
              <Text variant="caption" className="text-status-error mt-1">{state.errors.initial_date}</Text>
            )}
          </div>
          <div>
            <Text variant="label" className="block mb-1.5">{t('amlCustomerFieldDateCompanyEstablished')}</Text>
            <DatePicker
              value={state.established_date}
              onChange={(v) => setField('established_date', v, 'established_date')}
              placeholder={t('amlCustomerPlaceholderSelectDate')}
              maxDate={TODAY_ISO}
            />
            {state.errors.established_date && (
              <Text variant="caption" className="text-status-error mt-1">{state.errors.established_date}</Text>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TextField
            label={t('amlCustomerFieldCompanyLegalRepresentative')}
            value={state.legal_representative}
            onChange={(e) => setField('legal_representative', e.target.value, 'legal_representative')}
            placeholder={t('amlCustomerPlaceholderCompanyLegalRep')}
            error={state.errors.legal_representative}
          />
          <TextField
            label={t('amlCustomerFieldMainAddress')}
            value={state.address}
            onChange={(e) => setField('address', e.target.value, 'address')}
            placeholder={t('amlCustomerPlaceholderMainAddress')}
            error={state.errors.address}
          />
          <TextField
            label={t('amlCustomerFieldServicesAndProduct')}
            value={state.service_and_product}
            onChange={(e) => setField('service_and_product', e.target.value)}
            placeholder={t('amlCustomerPlaceholderServicesProduct')}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextArea
            label={t('amlCustomerFieldTypeOfBusinessActivity')}
            value={state.activity_type}
            onChange={(e) => setField('activity_type', e.target.value)}
            placeholder={t('amlCustomerPlaceholderTypeOfActivity')}
            rows={3}
            resize="vertical"
          />
          <TextArea
            label={t('amlCustomerFieldNotesAmlRev')}
            value={state.notes}
            onChange={(e) => setField('notes', e.target.value)}
            rows={3}
            resize="vertical"
            placeholder={t('amlCustomerPlaceholderNotesAmlRev')}
          />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <TextField
            label={t('amlCustomerFieldContact')}
            value={state.contact}
            onChange={(e) => setField('contact', e.target.value, 'contact')}
            placeholder={t('amlCustomerPlaceholderContact')}
            error={state.errors.contact}
          />
          <TextField
            label={t('amlCustomerFieldEmail')}
            type="email"
            value={state.email}
            onChange={(e) => setField('email', e.target.value, 'email')}
            placeholder={t('amlCustomerPlaceholderEmailAddress')}
            error={state.errors.email}
          />
          <div>
            <Text variant="label" className="block mb-1.5">{t('amlCustomerFieldPhone')}</Text>
            <PatternFormat
              value={state.phone}
              onValueChange={(v) => setField('phone', v.value || PHONE_EMPTY)}
              format="+### (###) ###-####"
              allowEmptyFormatting
              mask="_"
              placeholder={t('amlCustomerPlaceholderEnterPhone')}
              className="w-full px-3 py-2 rounded-xl bg-surface-primary border border-subtle text-content text-sm focus:outline-none focus:ring-2 focus:ring-brand/40 focus:border-brand transition-colors"
            />
          </div>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Text variant="label">{t('amlCustomerFieldLocationsDetail')}</Text>
            <Button type="button" variant="success" size="sm" leftIcon={<Plus size={14} />} onClick={addLocation}>
              {t('amlCustomerBtnAdd')}
            </Button>
          </div>
          {state.errors.locations && (
            <Text variant="caption" className="text-status-error mb-2">{state.errors.locations}</Text>
          )}
          {state.locations.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {state.locations.map((loc, idx) => {
                const locked = !!loc.id && lockedLocationIds.has(loc.id);
                return (
                  <div key={loc.id ?? `new-${idx}`} className="flex gap-2">
                    <TextField
                      value={loc.name}
                      onChange={(e) => handleLocationChange(idx, e.target.value)}
                      placeholder={t('amlCustomerPlaceholderLocationName')}
                    />
                    {!locked && (
                      <IconButton
                        icon={<Trash2 size={18} />}
                        variant="danger"
                        aria-label={t('amlCustomerAriaLabelActions')}
                        type="button"
                        onClick={() => removeLocation(idx)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <Switch
          label={`${t('amlCustomerFieldStatus')} — ${state.status ? t('amlCustomerStatusActive') : t('amlCustomerStatusInactive')}`}
          checked={state.status}
          onChange={(checked) => setField('status', checked)}
        />

        <div className="flex items-center justify-end gap-3 pt-4 border-t border-subtle">
          <Button variant="ghost" size="lg" type="button" onClick={onClose}>
            {t('amlBtnCancel')}
          </Button>
          <Button variant="primary" size="lg" type="submit" loading={state.submitting || loading}>
            {isEdit ? t('amlCustomerBtnSaveChanges') : t('amlCustomerBtnCreateCustomer')}
          </Button>
        </div>
      </form>
    </SlidePanel>
  );
};

export default CustomerForm;
